var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DOB_INPUT_DATE, getConfig, ISO_DATE_FORMAT, LOCAL_USER_UPDATED_EVENT, parseCustomDate, } from "@jti/fe-common";
import { BrowserStorage, getUser, User } from "../../utils";
import { prepareUserProfile, prepareUserProfileEvo } from "./utils/prepareProfileData/prepareProfileData";
import { CasBaseApi } from "./CasBaseApi";
import { Endpoints, EvoEndpoints } from "./CasEndpoints";
import { handleAddPlusToPhone } from "./utils";
export class CasProfileApi extends CasBaseApi {
    constructor(baseUrl, tenantToken, ciamToken, userUuid) {
        super(baseUrl, tenantToken, ciamToken, userUuid);
        this.tenantToken = tenantToken;
        this.ciamToken = ciamToken;
        this.userUuid = userUuid;
        this.name = "CasProfileApi";
        this.Endpoints = this.isEvo ? EvoEndpoints : Endpoints;
        // TODO: Remove this one when EVO will implement that (DESP-66410)
        this.defaultMeta = { allow_date_of_birth_update: true };
    }
    static init() {
        const config = getConfig();
        const user = getUser();
        return new CasProfileApi(config.CAS_URL, config.CAS_X_DES_AK_TENANT_TOKEN, user.ciam_token, user.uuid || "");
    }
    getProfile(isCached) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { data, meta } = yield this.request(this.replaceUuid(this.Endpoints.ProfilePath), {}, true, isCached);
                const preparedMeta = Object.assign(Object.assign({}, this.defaultMeta), meta);
                new User(new BrowserStorage()).setUser(Object.assign(Object.assign({}, data.consumer), { is_age_verified: preparedMeta.is_age_verified }));
                window.dispatchEvent(new Event(LOCAL_USER_UPDATED_EVENT));
                const preparedData = prepareUserProfileEvo(data, preparedMeta);
                return Promise.resolve(preparedData);
            }
            const response = yield this.request(this.replaceUuid(this.Endpoints.ProfilePath), {}, undefined, isCached);
            new User(new BrowserStorage()).setUser({ is_age_verified: response.is_age_verified });
            const preparedData = prepareUserProfile(response);
            return preparedData;
        });
    }
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.Endpoints.Logout, {
                method: this.isEvo ? "DELETE" : "POST",
            }, this.isEvo);
        });
    }
    changePassword(uuid, email, newPassword, currentPassword, confirmPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                return this.request(this.Endpoints.ChangePassword, {
                    method: "POST",
                    body: JSON.stringify({
                        new_password: newPassword,
                        current_password: currentPassword,
                        new_password_confirmation: confirmPassword,
                    }),
                }, true);
            }
            return this.request(this.replaceUuid(this.Endpoints.ChangePassword, uuid), {
                method: "POST",
                body: JSON.stringify({
                    email,
                    new_password: newPassword,
                    old_password: currentPassword,
                    new_password_confirmation: confirmPassword,
                }),
            });
        });
    }
    updateEmail(email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.replaceUuid(this.Endpoints.UpdateEmail), {
                method: "POST",
                body: JSON.stringify({ email }),
            }, true);
        });
    }
    updatePhone(phone, pvs_uuid, avp_uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const data = yield this.request(this.replaceUuid(this.Endpoints.UpdatePhone), {
                    method: "PATCH",
                    body: JSON.stringify({
                        phone: handleAddPlusToPhone(phone),
                        action_verification_uuid: pvs_uuid,
                        age_verification_uuid: avp_uuid,
                    }),
                }, true);
                return Promise.resolve({
                    personalInfo: {
                        firstname: data.first_name,
                        lastname: data.last_name,
                        dob: data.date_of_birth,
                        email: data.email,
                        gender: data.gender,
                        telephone: data.phone,
                    },
                    addresses: data.addresses,
                    statusCode: data.statusCode,
                });
            }
            return this.request(this.replaceUuid(this.Endpoints.UpdatePhone), {
                method: "POST",
                body: JSON.stringify({ phone, pvs_uuid, avp_uuid }),
            });
        });
    }
    updateAgeVerification(avp_uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestBody = this.isEvo ? { age_verification_uuid: avp_uuid } : { avp_uuid };
            return this.request(this.replaceUuid(this.Endpoints.UpdateAv), {
                method: "POST",
                body: JSON.stringify(requestBody),
            }, this.isEvo);
        });
    }
    actionVerificationItem(body) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/action-verifications/items", {
                method: "POST",
                body: JSON.stringify(body),
            }, true);
        });
    }
    sendOTC(phone_1) {
        return __awaiter(this, arguments, void 0, function* (phone, otpChannel = "sms") {
            if (this.isEvo) {
                const response = yield this.actionVerificationItem({
                    channel: otpChannel || "sms",
                    identifier: handleAddPlusToPhone(phone),
                });
                return {
                    phone_verification_uuid: response.data.uuid,
                };
            }
            const { phone_verification_uuid } = yield this.request("/send-otc", {
                body: JSON.stringify({
                    phone_number: phone,
                }),
                method: "POST",
            }, true);
            return { phone_verification_uuid };
        });
    }
    sendEmailOTC(email) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const response = yield this.actionVerificationItem({
                    channel: "email",
                    identifier: email,
                });
                return { email_verification_uuid: response.data.uuid };
            }
            const { email_verification_uuid } = yield this.request("/send-email-otc", {
                body: JSON.stringify({
                    email,
                }),
                method: "POST",
            }, true);
            return {
                email_verification_uuid,
            };
        });
    }
    verifiedPhone(phone) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.replacePhone(this.Endpoints.VerifyPhone, phone), {
                method: "GET",
            }, true);
        });
    }
    updatePersonalData(firstname_1, lastname_1, date_of_birth_1, gender_1) {
        return __awaiter(this, arguments, void 0, function* (firstname, lastname, date_of_birth, gender, dobFormat = DOB_INPUT_DATE) {
            if (this.isEvo) {
                const { data, statusCode, meta } = yield this.request(this.replaceUuid(this.Endpoints.UpdateProfile), {
                    body: JSON.stringify({
                        first_name: firstname,
                        last_name: lastname,
                        date_of_birth: date_of_birth &&
                            parseCustomDate({
                                date: date_of_birth,
                                initialFormat: dobFormat,
                                resultFormat: ISO_DATE_FORMAT,
                            }),
                        gender,
                    }),
                    method: "POST",
                }, true);
                const preparedMeta = Object.assign(Object.assign({}, this.defaultMeta), meta);
                return Promise.resolve({
                    is_age_verified: preparedMeta.is_age_verified,
                    is_pending_email_verification: data.is_pending_email_verification,
                    personal_info: {
                        email: data.consumer.email,
                        firstname: data.consumer.first_name,
                        lastname: data.consumer.last_name,
                        date_of_birth: data.consumer.date_of_birth,
                        phone: data.consumer.phone,
                        telephone: data.consumer.phone,
                        gender: data.consumer.gender,
                    },
                    addresses: data.consumer.addresses,
                    extra: {},
                    purposes: data.purposes,
                    statusCode,
                    meta: preparedMeta,
                });
            }
            return this.request(this.replaceUuid(this.Endpoints.UpdateProfile), {
                body: JSON.stringify({
                    firstname,
                    lastname,
                    date_of_birth,
                    gender,
                }),
                method: "POST",
            }, true);
        });
    }
    requestDiscount() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.replaceUuid(this.Endpoints.GeneratePromoCode), {
                method: "POST",
            }, true);
        });
    }
    requestWalletUrl(type) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.replaceUuid(this.Endpoints.Wallet), {
                method: "POST",
                body: JSON.stringify({ type }),
            });
        });
    }
}
//# sourceMappingURL=CasProfileApi.js.map